import React, { Dispatch, SetStateAction } from 'react';
import { gql } from '@ts-gql/tag';
import { useMutation } from '@ts-gql/apollo';

import { Text } from '@reckon-web/text';
import { AlertDialog } from '@reckon-web/modal-dialog';
import { useToasts } from '@reckon-web/toast';

import { User } from '../../types';
import { useError } from '../../../../utilities/hooks/useError';

const MFA_RESET = gql`
  mutation MfaReset($input: String!) {
    mfaReset(email: $input)
  }
` as import('../../../../../__generated__/ts-gql/MfaReset').type;

type ResetMfaDialogProps = {
  editUser: User | null;
  isResetMfaDialogOpen: boolean;
  setResetMfaDialogOpen: Dispatch<SetStateAction<boolean>>;
};

export const ResetMfaDialog = ({
  editUser,
  isResetMfaDialogOpen,
  setResetMfaDialogOpen,
}: ResetMfaDialogProps) => {
  const { addToast } = useToasts();
  const handleError = useError();
  const [resetMFA, { loading }] = useMutation(MFA_RESET);

  const resetMfaActions = {
    confirm: {
      label: 'Continue',
      action: () => {
        const { email } = { ...editUser };
        if (!email) return;

        resetMFA({
          variables: {
            input: email,
          },
        })
          .then(() => {
            setResetMfaDialogOpen(false);
            addToast({
              title: 'Reset multi-factor authentication success',
              message:
                'The user will be prompted to set up MFA next time they log in',
              tone: 'positive',
            });
          })
          .catch((error) => {
            handleError({ error, title: 'Unable to reset MFA' });
          });
      },
      loading,
    },
    cancel: { label: 'Cancel', action: () => setResetMfaDialogOpen(false) },
  };

  return (
    <AlertDialog
      actions={resetMfaActions}
      isOpen={isResetMfaDialogOpen}
      title="Reset multi-factor authentication"
    >
      <Text>
        User {editUser?.fullName} will be prompted to set up multi-factor
        authentication (MFA) when they next log in
      </Text>
    </AlertDialog>
  );
};
